import { Injectable } from '@angular/core';
import { Subject, Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { UserManager, User, WebStorageStateStore, UserManagerSettings } from 'oidc-client';
import * as debug from './debug.service';
import { appUrl, auth0Config } from 'src/environments/environment';
import { HttpClient, HttpHeaders, HttpRequest } from '@angular/common/http';
import { IUserProfile } from '../responses/iuser-profile';
import { MenuService } from './menu.service';
import { ProfileDbService } from './profile-db.service';
import { getCurrentWindowBaseUrl, fallbackMenu } from '../utils/utility.common';
import { ICreateUserRequest } from '../requests/icreate-user-request';
import { IVerifyCheckoutResponse } from '../responses/imenu';
import { LoggingService } from './logging.service';

@Injectable({ providedIn: 'root' })
export class SecurityService {

	private readonly userManager: UserManager;
	private loginChangedSubject = new Subject<boolean>();

	appUrl = appUrl;
	user: User;
	loginChanged = this.loginChangedSubject.asObservable();
	userProfile: IUserProfile;

	constructor(private readonly httpClient: HttpClient
		, private readonly menuService: MenuService
		, private readonly profileDbService: ProfileDbService
		, private readonly loggingService: LoggingService
	) {

		// Log.logger = logService; // for oidc-client usage
		debug.log('008 SecurityService instantiated');

		//// load guest profile
		// this.getUserProfile(appUrl.guestProfile, `069.0 MenuResolverService.resolve`);

		//// Uncomment when using local identityServer
		// this.userManager = new UserManager(this.getUserManagerConfig());
		this.userManager = new UserManager(this.getAuth0Config());
		this.userManager.events.addAccessTokenExpired(_ => {

			debug.log(`069 SecurityService constructor isLoggedIn: false`);

			this.loginChangedSubject.next(false);

		});

		this.userManager.events.addUserLoaded(user => {

			debug.log(`070 SecurityService addUserLoaded user:`, user);

			if (this.user !== user) {

				const isLoggedIn = !!user && !user.expired;

				this.user = user;

				debug.log(`071 SecurityService constructor isLoggedIn: ${isLoggedIn}`);

				this.loginChangedSubject.next(isLoggedIn);
			}
		});

		// this.loadSecurityContext('SecurityService constructor');
	}

	getUserProfile(url: string, caller: string): Observable<IUserProfile> {

		debug.log(`${caller}->SecurityService.getUserProfile url: ${url}`);

		return this.httpClient.get<IUserProfile>(url)
			.pipe(
				catchError(err => {

					debug.log(`${caller}->SecurityService.getUserProfile using fallbackmenu url: ${url} error:`, err);

					return of(fallbackMenu);

				}),
				tap(async (profile: IUserProfile) => {

					debug.log(`${caller}->SecurityService.getUserProfile:`, profile);

					await this.setUserProfile(profile, 'getUserProfile');

				})
			);
	}

	verifyCheckoutResponse(hostedpage: string, caller: string): Observable<IVerifyCheckoutResponse> {

		const url = `${this.appUrl.pabblyVerifyCheckout}/${hostedpage}`;

		debug.log(`${caller}->SecurityService.verifySubscription url: ${url}`);

		return this.httpClient.put<IVerifyCheckoutResponse>(url, '');

	}

	/** Initialize menuService and save profile to local db */
	async setUserProfile(userProfile: IUserProfile, caller: string): Promise<IUserProfile> {

		debug.log(`${caller}->SecurityService.setUserProfile:`, userProfile);

		this.menuService.initialize(userProfile, `${caller}->SecurityService.setUserProfile`);

		await this.profileDbService.store(MenuService.isLoggedIn, userProfile, 'SecurityService.setUserProfile');

		return Promise.resolve(userProfile);

	}

	signOutRedirectCallback(): Promise<any> {

		return this.userManager.signoutRedirectCallback();

	}

	async login(): Promise<any> {

		try {

			return this.userManager.signinRedirect();

		} catch (e) {

			debug.error('login failed!', e);

			return Promise.resolve();

		}
	}

	async isLoggedIn(caller: string): Promise<boolean> {

		const user = await this.userManager.getUser();
		const userCurrent = !!user && !user.expired;

		debug.log(`${caller}->SecurityService.isLoggedIn: ${userCurrent}`);

		if (this.user !== user) {

			debug.log(`${caller}->SecurityService.isLoggedIn triggerring: this.loginChangedSubject.next`);
			this.loginChangedSubject.next(userCurrent);

		}

		debug.log(`${caller}->SecurityService.isLoggedIn this.loginChangedSubject.next triggerred`);

		this.user = user;

		MenuService.isLoggedIn = userCurrent;
		MenuService.accessToken = user ? user.access_token : '';

		return Promise.resolve(userCurrent);
	}

	async completeLogin(): Promise<ICreateUserRequest> {
		await this.profileDbService.deleteNonGuestUsers();

		this.user = await this.userManager.signinRedirectCallback();
		const userCurrent = !!this.user && !this.user.expired;

		debug.log(`SecurityService completeLogin isLoggedIn: ${userCurrent}`);

		this.loginChangedSubject.next(userCurrent);

		MenuService.isLoggedIn = userCurrent;
		MenuService.accessToken = this.user ? this.user.access_token : '';

		return userCurrent ? Promise.resolve(this.user.profile) : Promise.resolve(undefined);
	}

	async logout(postLogoutMsg?: Error): Promise<any> {

		debug.log(`SecurityService logout`);

		await this.profileDbService.clearAll();

		if (!!postLogoutMsg) {
			await this.loggingService.logError(postLogoutMsg, `SecurityService->logout`);
		}

		return this.userManager.signoutRedirect();
	}

	async completeLogout() {

		MenuService.isLoggedIn = false;
		MenuService.accessToken = '';

		this.user = null;

		debug.log(`SecurityService completeLogout isLoggedIn: false`);

		this.loginChangedSubject.next(false);

		await this.profileDbService.clearAll();

		return this.userManager.signoutRedirectCallback();
	}

	async getAccessToken() {

		const user = await this.userManager.getUser();

		if (!!user && !user.expired) {

			MenuService.accessToken = user.access_token;

			return user.access_token;

		}

		return null;
	}

	async getSecurityHeader(req: HttpRequest<any> = null): Promise<any> {

		const token = await this.getAccessToken();

		if (!token) {

			return Promise.resolve();

		}

		const headers = new HttpHeaders()
			.set('Authorization', `Bearer ${token}`)
			.set('content-type', 'application/json');

		if (req) {

			const authReq = req.clone({ headers });

			return Promise.resolve(authReq);
		}

		return Promise.resolve(headers);
	}

	isImageExpired(): boolean {

		return true;

	}

	getImageTimeStamp(): Date {

		return new Date();

	}

	getGuestProfile(): Observable<IUserProfile> {

		const url = `${appUrl.guestProfile}`;

		return this.httpClient.get<IUserProfile>(url);

	}

	getAuth0Config(): UserManagerSettings {
		// oauth0
		const currentWindowBaseUrl = getCurrentWindowBaseUrl();
		const config = {
			authority: auth0Config.identityServer,
			client_id: auth0Config.identityServerClientId,
			redirect_uri: `${currentWindowBaseUrl}/signin-callback`,
			scope: auth0Config.apiScope,
			response_type: 'code',
			metadata: {
				issuer: `${auth0Config.identityServer}`,
				authorization_endpoint: `${auth0Config.identityServer}authorize?audience=${auth0Config.apiIdentifier}`,
				jwks_uri: `${auth0Config.identityServer}.well-known/jwks.json`,
				token_endpoint: `${auth0Config.identityServer}oauth/token`,
				userinfo_endpoint: `${auth0Config.identityServer}userinfo`,
				end_session_endpoint: `${auth0Config.identityServer}v2/logout?client_id=${auth0Config.identityServerClientId}&returnTo=${encodeURI(currentWindowBaseUrl)}/signout-callback`
			}
		};

		return config;

	}

	//// Uncomment when using local identityServer
	// private getUserManagerConfig(): UserManagerSettings {

	// 	const currentWindowBaseUrl = getCurrentWindowBaseUrl();
	// 	// identityServer local
	// 	const config = {
	// 		authority: idpConfig.identityServer,
	// 		client_id: idpConfig.identityServerClientId,
	// 		redirect_uri: `${currentWindowBaseUrl}/signin-callback`,
	// 		scope: idpConfig.apiScope,
	// 		response_type: 'code',
	// 		post_logout_redirect_uri: `${currentWindowBaseUrl}/signout-callback`,
	// 		userStore: new WebStorageStateStore({ store: window.localStorage as any }),
	// 		automaticSilentRenew: true,
	// 		silent_redirect_uri: `${currentWindowBaseUrl}/assets/silent-callback.html`
	// 	};

	// 	// // oauth0
	// 	// const config = {
	// 	//     authority: appConfig.identityServer,
	// 	//     client_id: appConfig.identityServerClientId,
	// 	//     redirect_uri: `${currentWindowBaseUrl}/signin-callback`,
	// 	//     scope: 'openid profile projects-api',
	// 	//     response_type: 'code',
	// 	//     metadata: {
	// 	//         issuer: `${appConfig.identityServer}`,
	// 	//         authorization_endpoint: `${appConfig.identityServer}authorize?audience=projects-api`,
	// 	//         jwks_uri: `${appConfig.identityServer}.well-known/jwks.json`,
	// 	//         token_endpoint: `${appConfig.identityServer}oauth/token`,
	// 	//         userinfo_endpoint: `${appConfig.identityServer}userinfo`,
	// 	//         end_session_endpoint: `${appConfig.identityServer}v2/logout?client_id=${appConfig.identityServerClientId}&returnTo=${encodeURI(currentWindowBaseUrl)}/signout-callback`
	// 	//     }
	// 	// };

	// 	return config;

	// }
}
